"use client";

import { useCallback, useRef, useState } from "react";
import { trpc } from "../trpc";

interface Result {
  movableIds: string[];
  nonMovableCount: number;
  isLoading: boolean;
  hasChecked: boolean;
}

// Permissions API allows max 30 checks per request (asset_ids × permission_levels).
// We send one permission level, so cap at 25 IDs per request for safety margin.
const PERMISSION_BATCH_SIZE = 25;

/**
 * Checks can_edit permissions for a batch of type-prefixed asset IDs
 * (e.g. "zap:123", "table:456", "folder:abc"). Groups by asset type
 * and makes one permission check per type. Folders are assumed movable
 * since there's no granular edit permission for them today.
 *
 * Call `check(ids)` explicitly when the permission check should run
 * (e.g. when the move modal opens). This keeps the check event-driven
 * rather than coupled to the render cycle via useEffect.
 */
export function useBulkCheckMovePermissions(): Result & {
  check: (ids: string[]) => Promise<void>;
} {
  const utils = trpc.useUtils();
  const requestIdReference = useRef(0);
  const [result, setResult] = useState<Result>({
    movableIds: [],
    nonMovableCount: 0,
    isLoading: false,
    hasChecked: false,
  });

  const check = useCallback(
    async (ids: string[]) => {
      const requestId = ++requestIdReference.current;
      if (ids.length === 0) {
        setResult({
          movableIds: [],
          nonMovableCount: 0,
          isLoading: false,
          hasChecked: true,
        });
        return;
      }

      setResult({
        movableIds: [],
        nonMovableCount: 0,
        isLoading: true,
        hasChecked: false,
      });

      const grouped: Record<string, { rawId: string; typedId: string }[]> = {};
      for (const typedId of ids) {
        const colonIndex = typedId.indexOf(":");
        const type = typedId.slice(0, colonIndex);
        const rawId = typedId.slice(colonIndex + 1);
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push({ rawId, typedId });
      }

      const movable: string[] = [];

      // Zaps aren't on FGA yet — they inherit permissions through folders.
      // Skip the FGA check and assume movable (same as folders).
      // TODO (ASM-4151): Remove "zap" from this set once Zaps are on FGA.
      const skipFgaCheck = new Set(["folder", "zap"]);

      const typesToCheck = Object.entries(grouped).filter(
        ([type]) => !skipFgaCheck.has(type),
      );

      for (const type of skipFgaCheck) {
        if (grouped[type]) {
          for (const item of grouped[type]) {
            movable.push(item.typedId);
          }
        }
      }

      const batches = typesToCheck.flatMap(([assetType, items]) => {
        const chunks: { rawId: string; typedId: string }[][] = [];
        for (
          let index = 0;
          index < items.length;
          index += PERMISSION_BATCH_SIZE
        ) {
          chunks.push(items.slice(index, index + PERMISSION_BATCH_SIZE));
        }
        return chunks.map((chunk) => ({ assetType, chunk }));
      });

      // Each batch gets its own try/catch so a single failing asset type
      // doesn't block the entire check. On failure the batch is treated as
      // non-movable (fail-closed) — the user sees those assets as
      // un-movable rather than being allowed to attempt an unverified move.
      // Backend move endpoints still enforce auth as a second layer.
      await Promise.all(
        batches.map(async ({ assetType, chunk }) => {
          try {
            const data = await utils.permission.checkPermissions.fetch({
              assetIds: chunk.map((item) => item.rawId),
              assetType: assetType as any,
              permissionLevels: ["can_edit"],
            });
            if (!data?.results) return;
            const permMap = new Map<string, boolean>();
            for (const r of data.results) {
              permMap.set(
                String(r.assetId),
                !!(r.permissionLevels as Record<string, boolean>)?.canEdit,
              );
            }
            for (const item of chunk) {
              if (permMap.get(item.rawId)) {
                movable.push(item.typedId);
              }
            }
          } catch {
            // Permission check failed for this batch. Don't assume movable --
            // the user will see these assets as non-movable in the modal.
          }
        }),
      );

      if (requestId !== requestIdReference.current) return;

      setResult({
        movableIds: movable,
        nonMovableCount: ids.length - movable.length,
        isLoading: false,
        hasChecked: true,
      });
    },
    [utils.permission.checkPermissions],
  );

  return { ...result, check };
}
